<div class="knowledge-base">

	<!--
		Both panels are defined once here and rendered either side of the splitter on
		desktop, or one at a time as a master-detail stack on phones.
	-->
	<!-- #region List panel -->
	<ng-template #listPanel>
		<div class="kb-panel kb-list-panel">
			<div class="kb-list-header">
				<h3>Knowledge Base</h3>

				<div class="kb-search-bar">
					<p-iconfield class="kb-search">
						<p-inputicon>
							<fa-icon [icon]="faIcon.faMagnifyingGlass"></fa-icon>
						</p-inputicon>
						<input
							#searchInput
							pInputText
							type="search"
							placeholder="Search Documents..."
							(keyup)="searchDocuments(searchInput.value)"
							(search)="searchDocuments(searchInput.value)"
						/>
					</p-iconfield>

					@if (canCreateDoc()) {
						<p-button
							size="small"
							severity="success"
							label="New Document"
							styleClass="kb-new-doc"
							(onClick)="createDocument()"
						/>
					}
				</div>
			</div>
			<hr>

			<!-- #region File navigation -->
			<div class="kb-tree">
				@if (!hasSearchResults()) {
					<p class="kb-no-results">No results found.</p>
				}

				@for (folder of folders(); track folder.module) {
					<div class="kb-folder">
						<!-- The pencil is a sibling of the toggle, not a child: a button inside a button is invalid markup. -->
						<div class="kb-folder-row">
							<button
								type="button"
								class="kb-folder-header"
								[attr.aria-expanded]="folder.isExpanded"
								(click)="toggleFolder(folder.module)"
							>
								<fa-icon
									[icon]="folder.isExpanded
										? faIcon.faFolderOpen
										: faIcon.faFolder
									"
								></fa-icon>
								<span class="kb-folder-label">{{ folder.label }}</span>
							</button>

							@if (folder.canAuthor) {
								<p-button
									text
									rounded
									size="small"
									severity="secondary"
									styleClass="kb-folder-edit"
									[ariaLabel]="'New document in ' + folder.label"
									[pTooltip]="'New document in ' + folder.label"
									tooltipPosition="right"
									(onClick)="createDocumentInFolder(folder.module, $event)"
								>
									<fa-icon [icon]="faIcon.faPencil"></fa-icon>
								</p-button>
							}
						</div>

						@if (folder.isExpanded) {
							<div
								cdkDropList
								class="kb-doc-list"
								[cdkDropListDisabled]="!folder.canAuthor || isSearching()"
								(cdkDropListDropped)="reorderDocuments($event, folder.module)"
							>
								@for (doc of folder.docs; track doc.documentId) {
									<button
										cdkDrag
										type="button"
										class="kb-doc-row"
										[cdkDragDisabled]="!folder.canAuthor || isSearching()"
										[class.is-selected]="doc.documentId === selectedDoc()?.documentId"
										(click)="openDocument(doc)"
									>
										@if (folder.canAuthor && !isSearching()) {
											<fa-icon
												cdkDragHandle
												class="kb-drag-handle"
												[icon]="faIcon.faGripVertical"
											></fa-icon>
										} @else {
											<fa-icon
												class="kb-doc-icon"
												[icon]="faIcon.faFile"
											></fa-icon>
										}

										<span class="kb-doc-text">
											<span class="kb-doc-title">{{ doc?.title }}</span>
											@if (doc?.description) {
												<span class="kb-doc-description">{{ doc.description }}</span>
											}
										</span>
									</button>
								}
							</div>
						}
					</div>
				}
			</div>
			<!-- #endregion -->
		</div>
	</ng-template>
	<!-- #endregion -->

	<!-- #region Document panel -->
	<ng-template #docPanel>
		<div class="kb-panel kb-doc-panel">
			@if (hasPanelContent()) {
				<div class="kb-doc-header">
					<div class="kb-doc-heading">
						@if (isMobile()) {
							<p-button
								text
								size="small"
								severity="secondary"
								ariaLabel="Back to documents"
								(onClick)="closeDocument()"
							>
								<fa-icon [icon]="faIcon.faArrowLeft"></fa-icon>
							</p-button>
						}
						@switch (mode()) {
							@case ('create') { <h3>Create Document</h3> }
							@case ('edit') { <h3>Edit Document</h3> }
							@default { <h3>{{ selectedDoc()?.title }}</h3> }
						}
					</div>

					<div class="kb-doc-actions">
						@if (isViewingDoc()) {
							@if (canEdit()) {
								<p-button
									outlined
									size="small"
									severity="secondary"
									label="Edit"
									(onClick)="editDocument()"
								/>
								<p-button
									outlined
									size="small"
									severity="danger"
									label="Delete"
									(onClick)="confirmDelete()"
								/>
							}
						} @else {
							<p-selectbutton
								size="small"
								optionLabel="label"
								optionValue="value"
								[options]="formTabOptions"
								[allowEmpty]="false"
								[ngModel]="formTab()"
								(onChange)="setFormTab($event.value)"
							/>
						}

						@if (!isMobile()) {
							<p-button
								text
								rounded
								size="small"
								severity="secondary"
								styleClass="kb-close"
								ariaLabel="Close document"
								(onClick)="closeDocument()"
							>
								<fa-icon
									class="fa-lg"
									[icon]="faIcon.faXmark"
								></fa-icon>
							</p-button>
						}
					</div>
				</div>
				<hr>

				@if (isViewingDoc()) {
					<!-- #region View Document -->
					<div class="kb-doc-body">
						<pcg-knowledge-base-doc-view
							[docTitle]="$safeNavigationMigration(selectedDoc()?.title)"
							[description]="$safeNavigationMigration(selectedDoc()?.description)"
							[sections]="$safeNavigationMigration(selectedDoc()?.sections)"
						></pcg-knowledge-base-doc-view>
					</div>
					<!-- #endregion -->
				} @else {
					<!--
						#region Create and Edit Document
						Both faces stay mounted and are toggled with `hidden`, so switching to
						Preview never tears down the Trix editor and loses what has been typed.
					-->
					<div
						class="kb-doc-body"
						[hidden]="!isPreviewTab()"
					>
						<pcg-knowledge-base-doc-view
							[docTitle]="$safeNavigationMigration(previewDoc()?.title)"
							[description]="$safeNavigationMigration(previewDoc()?.description)"
							[sections]="$safeNavigationMigration(previewDoc()?.sections)"
						></pcg-knowledge-base-doc-view>
					</div>

					<form
						class="kb-form"
						[formGroup]="formGroup"
						[hidden]="isPreviewTab()"
					>
						<div class="kb-form-fields">
							<div class="form-row">
								<pcg-control-group
									class="doc-module-ctnr required-field"
									label="Title"
								>
									<input
										type="text"
										placeholder="Add a Title..."
										class="form-control"
										formControlName="title"
										pcgControlRef
										maxlength="50"
									/>
								</pcg-control-group>

								<pcg-control-group
									class="doc-module-ctnr required-field"
									label="Module"
								>
									<pcg-select
										pcgControlRef
										[multiple]='false'
										[items]="knowledgeBaseModuleItems()"
										formControlName='module'
										placeholder="Select a Module"
										(change)="onModuleChange()"
									></pcg-select>
								</pcg-control-group>

								<pcg-control-group
									class="doc-module-ctnr required-field"
									label="Minimum Permission"
									labelTooltip="Only users with access to this module and with this permission level or higher will be able to view this document."
									placement="right"
								>
									<pcg-select
										pcgControlRef
										[multiple]='false'
										[items]="permissionItems()"
										formControlName='permissionRole'
										placeholder="Select a Permission Role"
									></pcg-select>
								</pcg-control-group>
							</div>

							<div class="form-row">
								<pcg-control-group label="Description">
									<textarea
										placeholder="Add a Description..."
										class="form-control"
										formControlName="description"
										pcgControlRef
										maxlength="500"
									></textarea>
								</pcg-control-group>
							</div>

							<!--
								#region Sections
								The article body. Each section is a passage of text and the video that
								illustrates it, which is what lets a walkthrough alternate the two
								instead of stranding a single video at the bottom of the page.
							-->
							<div formArrayName="sections">
								@for (row of sectionRows(); track row.editorKey) {
									<div
										class="kb-section"
										[formGroupName]="row.index"
									>
										<div class="kb-section-header">
											<span class="kb-section-title">Section {{ row.position }}</span>

											<div class="kb-section-actions">
												<p-button
													text
													rounded
													size="small"
													severity="secondary"
													ariaLabel="Move section up"
													pTooltip="Move up"
													tooltipPosition="left"
													[disabled]="!row.canMoveUp"
													(onClick)="moveSection(row.index, -1)"
												>
													<fa-icon [icon]="faIcon.faArrowUp"></fa-icon>
												</p-button>

												<p-button
													text
													rounded
													size="small"
													severity="secondary"
													ariaLabel="Move section down"
													pTooltip="Move down"
													tooltipPosition="left"
													[disabled]="!row.canMoveDown"
													(onClick)="moveSection(row.index, 1)"
												>
													<fa-icon [icon]="faIcon.faArrowDown"></fa-icon>
												</p-button>

												@if (row.canRemove) {
													<p-button
														text
														rounded
														size="small"
														severity="danger"
														ariaLabel="Remove section"
														pTooltip="Remove section"
														tooltipPosition="left"
														(onClick)="removeSection(row.index)"
													>
														<fa-icon [icon]="faIcon.faTrash"></fa-icon>
													</p-button>
												}
											</div>
										</div>

										<div class="form-row">
											<pcg-control-group
												label="Header"
												labelTooltip="Optional heading shown above this section, for naming a step in a walkthrough. Leave it blank to run straight on from the section above."
												placement="right"
											>
												<input
													pcgControlRef
													formControlName="header"
													class="form-control"
													type="text"
													placeholder="Add a Header..."
													maxlength="100"
												/>
											</pcg-control-group>

											<pcg-control-group
												label="Video ID"
												labelTooltip="Input the Vimeo video ID here. This ID can be found in the published Vimeo video URL. Leave it blank for a section that is text only."
												placement="right"
											>
												<input
													pcgControlRef
													formControlName="videoId"
													class="form-control"
													type="text"
												/>
											</pcg-control-group>
										</div>

										<div class="form-row">
											<pcg-control-group label="Content">
												<pcg-text-editor
													[editorValue]="row.content"
													[imageFolder]="'KnowledgeBase'"
													[minHeight]="'10em'"
													(pcgFileChange)="setImageInFormGroup($event)"
												></pcg-text-editor>
											</pcg-control-group>
										</div>
									</div>
								}
							</div>

							<p-button
								outlined
								size="small"
								severity="secondary"
								label="Add Section"
								styleClass="kb-add-section"
								(onClick)="addSection()"
							/>
							<!-- #endregion -->
						</div>
					</form>

					<!--
						Outside the scrolling field area, so Save and Cancel stay reachable on
						either tab without hunting for the bottom of the form.
					-->
					<p-toolbar
						styleClass="kb-form-actions"
						[dt]="formActionDts"
					>
						<ng-template #start>
							@if (isError()) {
								<div class="alert alert-danger mb-0">{{ errorStr() }}</div>
							}
						</ng-template>
						<ng-template #end>
							<p-button
                                class="mr-2"
								outlined
								size="small"
								severity="secondary"
								label="Cancel"
								(onClick)="isCreateDoc() ? closeDocument() : cancelDocumentEdit()"
							/>
							<p-button
								size="small"
								severity="success"
								label="Save Changes"
								(onClick)="onSave()"
							/>
						</ng-template>
					</p-toolbar>
					<!-- #endregion -->
				}
			} @else {
				<div class="unselected-doc">
					<fa-icon [icon]="faIcon.faBook"></fa-icon>
					<p>Select an item to read</p>
				</div>
			}
		</div>
	</ng-template>
	<!-- #endregion -->

	@if (isMobile()) {
		<div class="kb-mobile">
			@if (showListPanel()) {
				<ng-container [ngTemplateOutlet]="listPanel"></ng-container>
			}
			@if (showDocPanel()) {
				<ng-container [ngTemplateOutlet]="docPanel"></ng-container>
			}
		</div>
	} @else {
		<p-splitter
			styleClass="kb-splitter"
			stateKey="pcgKnowledgeBaseSplitter"
			stateStorage="local"
			[panelSizes]="[24, 76]"
			[minSizes]="[16, 40]"
			[dt]="splitterDts"
		>
			<ng-template #panel>
				<ng-container [ngTemplateOutlet]="listPanel"></ng-container>
			</ng-template>
			<ng-template #panel>
				<ng-container [ngTemplateOutlet]="docPanel"></ng-container>
			</ng-template>
		</p-splitter>
	}
</div>

<!-- Delete confirmation -->
<p-dialog
	[visible]="isDeleteDialogOpen()"
	(visibleChange)="isDeleteDialogOpen.set($event)"
	header="Delete Document"
	[modal]="true"
	[style]="{ width: '30rem' }"
	[draggable]="false"
	[resizable]="false"
>
	<p class="mb-0">
		Are you sure you want to delete <strong>{{ selectedDoc()?.title }}</strong>?
		This cannot be undone.
	</p>

	<ng-template #footer>
		<p-button
			label="Cancel"
			severity="secondary"
			(click)="isDeleteDialogOpen.set(false)"
			outlined
		/>
		<p-button
			label="Delete"
			severity="danger"
			[loading]="isDeleting()"
			(click)="deleteDocument()"
			raised
		/>
	</ng-template>
</p-dialog>
